import { Maximize, Minimize } from "lucide-react";
import React, { useCallback } from "react";
import { WebViewTooltip } from "../../Pages/Diary/SubHeader/WebViewTooltip";
import { cn } from "../../../lib/utils";

const FullScreen = () => {
  const [isFullscreen, setIsFullscreen] = React.useState(
    !!document.fullscreenElement
  );
  const [fullscreenEnabled, setFullscreenEnabled] = React.useState(
    document.fullscreenEnabled
  );

  // Effect to handle iframe fullscreen attribute setup
  React.useEffect(() => {
    // If fullscreen is already enabled, no need to modify anything
    if (document.fullscreenEnabled) {
      setFullscreenEnabled(true);
      return;
    }

    // Check if we're in an iframe
    if (window.self !== window.top) {
      try {
        // Try to access the parent window and find the iframe that contains us
        let currentWindow: Window = window;
        let parentWindow = window.parent;

        while (parentWindow && parentWindow !== currentWindow) {
          try {
            // Look for iframes in the parent document
            const iframes = parentWindow.document.querySelectorAll("iframe");

            for (const iframe of iframes) {
              try {
                // Check if this iframe's contentWindow matches our window
                if (iframe.contentWindow === currentWindow) {
                  // Add the fullscreen allow attribute if it doesn't exist
                  const currentAllow = iframe.getAttribute("allow") || "";
                  if (!currentAllow.includes("fullscreen")) {
                    const newAllow = currentAllow
                      ? `${currentAllow}; fullscreen`
                      : "fullscreen";
                    iframe.setAttribute("allow", newAllow);

                    // Recheck fullscreen capability after a short delay
                    setTimeout(() => {
                      setFullscreenEnabled(document.fullscreenEnabled);
                    }, 100);
                  }
                  return;
                }
              } catch {
                // Cross-origin access might fail, continue to next iframe
                continue;
              }
            }

            // Move up to the next parent level
            currentWindow = parentWindow;
            parentWindow = parentWindow.parent;
          } catch {
            // Cross-origin access failed, break out of the loop
            break;
          }
        }
      } catch (e) {
        // Failed to access parent windows due to cross-origin restrictions
        console.warn("Could not access parent iframe to enable fullscreen:", e);
      }
    }
  }, []);

  const onFullscreenChange = useCallback(() => {
    if (isFullscreen) {
      // browser is fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if ((document as any).webkitExitFullscreen) {
        /* Safari */
        (document as any).webkitExitFullscreen();
      } else if ((document as any).msExitFullscreen) {
        /* IE11 */
        (document as any).msExitFullscreen();
      }
      setIsFullscreen(false);
    } else {
      const element: any = document.getElementById("root");
      if (element?.requestFullscreen) {
        element.requestFullscreen();
      } else if (element?.webkitRequestFullscreen) {
        /* Safari */
        element.webkitRequestFullscreen();
      } else if (element?.msRequestFullscreen) {
        /* IE11 */
        element.msRequestFullscreen();
      }
      setIsFullscreen(true);
    }
  }, [isFullscreen]);

  if (!fullscreenEnabled) {
    return null;
  }

  return (
    <WebViewTooltip
      content={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
      side="bottom"
      sideOffset={8}
    >
      <button
        className={cn("eds-ghost-icon-button", isFullscreen && "active")}
        onClick={onFullscreenChange}
        aria-label={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
      >
        {isFullscreen ? <Minimize /> : <Maximize />}
      </button>
    </WebViewTooltip>
  );
};

export default FullScreen;
